/**
 * AdminPortalWrapper.jsx
 * 
 * Wraps the Admin Portal with:
 * 1. AdminProvider - For isolated state management
 * 2. Error Boundary - For crash isolation (admin errors won't kill the session)
 * 3. Lazy Loading support - Admin code only loads when tab is accessed
 * 
 * CRITICAL: Uses optional chaining (?.) throughout to prevent 'ghost logouts'
 * when user object is briefly null during navigation/refresh.
 * 
 * Data is fetched on-demand when this tab is mounted.
 */

import React, { useEffect, Suspense } from 'react';
import { AdminProvider, useAdmin } from '../../context/AdminContext';
import { useUserRoleName } from '../../hooks/useUserRoleName';
import { Loader2 } from 'lucide-react';

// ============================================
// Error Boundary for Crash Isolation
// ============================================
class AdminErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null };
  }

  static getDerivedStateFromError(error) {
    // Update state so the next render shows the fallback UI
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    // Log error for debugging but DON'T crash the app
    console.error('[AdminPortal] Error caught by boundary:', error);
    console.error('[AdminPortal] Error info:', errorInfo);
    this.setState({ errorInfo });
  }

  handleRetry = () => {
    this.setState({ hasError: false, error: null, errorInfo: null });
  };

  render() {
    if (this.state.hasError) {
      return (
        <div className="p-6 bg-red-50 border border-red-200 rounded-lg m-4">
          <h3 className="text-lg font-semibold text-red-800 mb-2 flex items-center gap-2">
            <span>⚠️</span>
            Admin Portal Error
          </h3>
          <p className="text-red-600 mb-4">
            Something went wrong in the Admin Portal. Your session is still active and other features continue to work normally.
          </p>
          <div className="flex gap-2">
            <button
              onClick={this.handleRetry}
              className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700 transition-colors"
            >
              Try Again
            </button>
            <button
              onClick={() => window.location.reload()}
              className="px-4 py-2 bg-gray-600 text-white rounded hover:bg-gray-700 transition-colors"
            >
              Refresh Page
            </button>
          </div>
          <details className="mt-4 text-sm text-red-500">
            <summary className="cursor-pointer hover:text-red-700">Technical Details</summary>
            <pre className="mt-2 p-2 bg-red-100 rounded overflow-auto text-xs">
              {this.state.error?.toString()}
              {this.state.errorInfo?.componentStack && (
                <>
                  {'\n\nComponent Stack:'}
                  {this.state.errorInfo.componentStack}
                </>
              )}
            </pre>
          </details>
        </div>
      );
    }

    return this.props.children;
  }
}

// ============================================
// Loading Fallback Component
// ============================================
const AdminLoadingFallback = () => (
  <div className="flex items-center justify-center min-h-[400px]">
    <div className="text-center">
      <Loader2 className="h-8 w-8 animate-spin text-teal-600 mx-auto mb-2" />
      <p className="text-gray-500">Loading Admin Portal...</p>
    </div>
  </div>
);

// ============================================
// Inner Content Component (uses AdminContext)
// ============================================
const AdminPortalContent = ({ 
  children,
  user,
  // All the props that will be passed through to the admin content
  ...restProps 
}) => {
  const { loadAdminData, adminDataLoaded } = useAdmin();

  // Fetch admin data on mount (on-demand loading)
  useEffect(() => {
    // Safe check with optional chaining
    if (user?.id && !adminDataLoaded) {
      void 0 && console.log('[AdminPortal] Mounted - loading admin data on-demand');
      loadAdminData();
    }
  }, [user?.id, adminDataLoaded, loadAdminData]);

  // Safe permission check using role_id lookup
  const userRoleName = useUserRoleName();
  const isSuperAdmin = user?.is_super_admin === true || userRoleName === 'super admin';
  
  if (!isSuperAdmin) {
    return (
      <div className="p-6 bg-yellow-50 border border-yellow-200 rounded-lg m-4">
        <h3 className="text-lg font-semibold text-yellow-800 mb-2">
          Access Restricted
        </h3>
        <p className="text-yellow-600">
          You do not have permission to access the Admin Portal. 
          Please contact your administrator if you believe this is an error.
        </p>
      </div>
    );
  }

  // Render children (the actual admin content)
  // Clone children with admin context props if needed
  return (
    <>
      {React.Children.map(children, child => {
        if (React.isValidElement(child)) {
          return React.cloneElement(child, { user, ...restProps });
        }
        return child;
      })}
    </>
  );
};

// ============================================
// Main Wrapper Component
// ============================================
const AdminPortalWrapperInner = ({ 
  children, 
  user,
  ...props 
}) => {
  // Early return with safe check if no user (prevents crash during logout)
  if (!user?.id) {
    return (
      <div className="flex items-center justify-center min-h-[200px]">
        <p className="text-gray-500">Loading user data...</p>
      </div>
    );
  }

  return (
    <AdminErrorBoundary>
      <AdminProvider user={user}>
        <Suspense fallback={<AdminLoadingFallback />}>
          <AdminPortalContent user={user} {...props}>
            {children}
          </AdminPortalContent>
        </Suspense>
      </AdminProvider>
    </AdminErrorBoundary>
  );
};

// ============================================
// Helper HOC for Lazy Loading Admin Components
// ============================================
export const withAdminErrorBoundary = (WrappedComponent, componentName = 'Admin Component') => {
  return class extends React.Component {
    constructor(props) {
      super(props);
      this.state = { hasError: false, error: null };
    }

    static getDerivedStateFromError(error) {
      return { hasError: true, error };
    }

    componentDidCatch(error, errorInfo) {
      console.error(`[${componentName}] Error:`, error, errorInfo);
    }

    render() {
      if (this.state.hasError) {
        return (
          <div className="p-4 bg-red-50 border border-red-200 rounded">
            <p className="text-red-600 text-sm">
              Error loading {componentName}. 
              <button 
                onClick={() => this.setState({ hasError: false })}
                className="ml-2 text-red-800 underline"
              >
                Retry
              </button>
            </p>
          </div>
        );
      }
      return <WrappedComponent {...this.props} />;
    }
  };
};

export const AdminPortalWrapper = React.memo(AdminPortalWrapperInner);
export default AdminPortalWrapper;
